// =========================================================================
// One feedback loop — projection only. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderLoops` (source line 3034) and of the five
// `LOOPS[i].table()` builders (source lines 2914–2938).
//
// Two things the seed deliberately left unresolved are resolved here:
//
// 1. **Live figures.** `seed/loops.ts` carries each item's figure as a
//    `LiveFigure` — literal text interleaved with the demo's own expression
//    source (`{ expr: 'pastInterval().length' }`). The seed evaluates nothing;
//    this file binds each expression to the engine, keyed by the expression
//    text verbatim, so the binding table reads as a diff against the demo.
//    All 27 expressions across the five loops are bound.
//
// 2. **Panels.** Each panel carries an opaque `rowsExpr`. Seven of the eight
//    panels are rebuilt here against the loaded data; the eighth (bulletins)
//    is a chip list and is built in the scene because each chip opens a lake
//    document. Nothing is left as a literal fallback.
//
// One derivation is local rather than engine: `upgradeCandidateRows`. The
// engine's `upgradeCandidates` reads `servicecontract.type` off the
// **per-machine** Maximo record, and the port ships those one file per machine
// — a fleet-wide call would be 100 fetches. The six site contracts in
// `service.json` carry the identical string (verified against the shipped
// records), so the rule is applied from the site contract instead. See the
// function's own note.
//
// Transformation layer (layer 3): pure functions, no React, no fetching.
//
// @see docs/logic/loops/loop-page.md
// =========================================================================

import { BY_ID, FLEET, HEALTH, MINES } from '../../data/core';
import {
  CONDITION_BAND_LABEL,
  type ConditionBand,
} from '../../schema/machine';
import type { MineCode } from '../../schema/machine';
import type {
  Bulletin,
  CataloguePart,
  CustomerSatisfactionResponse,
  InventoryLine,
  KnowledgeArticle,
  MaximoContract,
  MeetingRecord,
  PartOrder,
  RosterEntry,
  ServiceCase,
  TechnicianJob,
  TechnicianPresence,
  Timesheet,
  TrainingRecord,
  WorkOrder,
} from '../../shared/useOemData';
import {
  EXCHANGE,
  FLEET_RULES,
  MSHORT,
  fmtk,
  groupNum,
  healthBand,
  healthOf,
  intervalOf,
  meanUtil,
  pastInterval,
  pmDue,
  predictedUsage,
  renewals,
  revenue,
  statusOf,
} from '../../shared/oem-engine';
import { INCIDENTS } from '../../shared/seed/incidents';
import { MODULE_STATUS_TOKENS, type LiveFigure } from '../../shared/seed/modules';
import type { LoopColumn, OemLoop } from '../../shared/seed/loops';

// -------------------------------------------------------------------------
// The data contract
// -------------------------------------------------------------------------

/**
 * Everything the five loop pages read, assembled by the scene from six lazy
 * domains: `work-orders`, `parts`, `people`, `service`, `comms` and `costs`.
 *
 * The union is loaded whole rather than per loop because the scene's loop id
 * is a route parameter — hooks cannot be conditional, and the module-level
 * fetch cache means walking all five loops costs one round of requests, not
 * five.
 */
export interface LoopCtx {
  readonly workOrders: readonly WorkOrder[];
  readonly techJobs: readonly TechnicianJob[];
  readonly cases: readonly ServiceCase[];
  readonly kb: readonly KnowledgeArticle[];
  readonly training: readonly TrainingRecord[];
  readonly contracts: readonly MaximoContract[];
  readonly bulletins: readonly Bulletin[];
  readonly inventory: readonly InventoryLine[];
  readonly orders: readonly PartOrder[];
  readonly parts: readonly CataloguePart[];
  readonly roster: readonly RosterEntry[];
  readonly techs: readonly TechnicianPresence[];
  readonly css: readonly CustomerSatisfactionResponse[];
  readonly timesheets: readonly Timesheet[];
  readonly meetings: readonly MeetingRecord[];
}

// -------------------------------------------------------------------------
// Live figures
// -------------------------------------------------------------------------

/**
 * Maximo site id → mine code. The six site contracts in `service.json` are
 * keyed by the customer's Maximo site, the fleet register by mine code.
 */
const SITE_TO_MINE: Readonly<Record<string, MineCode>> = {
  KALB: 'KG',
  BRKR: 'BR',
  MTIS: 'MI',
  OCHR: 'OH',
  GDEEP: 'GD',
  MTRD: 'MR',
};

/**
 * The site contract that governs a machine, or `undefined`.
 *
 * Every shipped `maximo/<id>.json` carries a `servicecontract.type` identical
 * to its site's row in `mx_contracts`; this is the fleet-wide lookup that lets
 * the commercial loop run without 100 per-machine fetches.
 */
function contractForMine(
  mine: MineCode,
  contracts: readonly MaximoContract[],
): MaximoContract | undefined {
  return contracts.find((c) => SITE_TO_MINE[c.siteid] === mine);
}

/** The demo's `ATTN` set, as the commercial loop applies it. */
const ATTENTION: readonly ConditionBand[] = ['poor', 'vpoor', 'major'];

/**
 * Machines the commercial loop would propose a contract upgrade on — the
 * engine's `upgradeCandidates`, evaluated from the site contract.
 *
 * The rule is the demo's, clause for clause: call-off terms, excluding a
 * battery machine already on battery call-off terms, and a condition band of
 * poor or worse.
 */
export function upgradeCandidateRows(ctx: Pick<LoopCtx, 'contracts'>) {
  return FLEET.filter((f) => {
    const contract = contractForMine(f.mine, ctx.contracts);
    if (!contract) return false;
    const callOff =
      /call-off/.test(contract.type) && !(f.battery && /Battery/.test(contract.type));
    return callOff && ATTENTION.includes(statusOf(f.machine_id));
  }).map((f) => {
    const worst = healthOf(f.machine_id)
      .slice()
      .sort((a, b) => a.health_score - b.health_score)[0];
    const contract = contractForMine(f.mine, ctx.contracts);
    return {
      machine: f.machine_id,
      model: f.model,
      mine: f.mine,
      site: MSHORT[f.mine],
      band: statusOf(f.machine_id),
      bandLabel: CONDITION_BAND_LABEL[statusOf(f.machine_id)],
      worstComponent: worst ? `${worst.component} ${worst.health_score}/100` : '—',
      terms: (contract?.type ?? '').split(' — ')[0],
      // Demo: `f.mine==='KG' ? … : …`. Both strings verbatim.
      proposal:
        f.mine === 'KG'
          ? 'Fleet Care (PM + condition monitoring) — the two coupling failures were both practice'
          : 'Bring the diesel fleet under Battery fleet care terms',
    };
  });
}

/** Machines the demo counts as white space — call-off terms, any condition. */
function callOffMachines(ctx: Pick<LoopCtx, 'contracts'>): number {
  return FLEET.filter((f) => {
    const contract = contractForMine(f.mine, ctx.contracts);
    return /call-off/.test(contract?.type ?? '') && !f.battery;
  }).length;
}

/**
 * Every `{ expr }` segment in `seed/loops.ts`, bound to its value.
 *
 * Keys are the demo expression **verbatim** — that is the whole point: a
 * reviewer can diff this table against the source demo line by line, and an
 * expression that is added to the seed and not bound here renders as `—`
 * rather than silently printing a wrong number.
 */
export function loopFigureValues(ctx: LoopCtx): Readonly<Record<string, string>> {
  const usage = predictedUsage(ctx);
  const rev = revenue(ctx);
  const contracts = renewals(ctx);
  const first = contracts[0];
  const inventoryLocations = new Set(ctx.inventory.map((i) => i.location));

  return {
    // ---- loop 1 · component lifecycle -------------------------------
    // Maximo child assets mirror the condition register one for one across
    // all 1,068 rows (`schema/service.ts` § MaximoChildAsset).
    'Object.values(B.mx).reduce((a,m)=>a+(m.children||[]).length,0)': groupNum(HEALTH.length),
    // One Maximo record per machine in the register.
    'Object.keys(B.mx).length': String(FLEET.length),
    'pastInterval().length': String(pastInterval().length),
    "HEALTH.filter(h=>h.band==='major'||h.band==='vpoor').length": String(
      HEALTH.filter((h) => healthBand(h) === 'major' || healthBand(h) === 'vpoor').length,
    ),
    'EXCHANGE.length': String(EXCHANGE.length),

    // ---- loop 2 · warranty, quality & campaigns ---------------------
    'B.cases.filter(c=>/Warranty/.test(c.type)).length': String(
      ctx.cases.filter((c) => /Warranty/.test(c.type)).length,
    ),
    'INCIDENTS.length': String(INCIDENTS.length),
    'B.meetings.length': String(ctx.meetings.length),
    'B.bulletins.length': String(ctx.bulletins.length),

    // ---- loop 3 · fleet learning ------------------------------------
    'WOS.filter(w=>/^Closed/.test(w.status)).length.toLocaleString()': groupNum(
      ctx.workOrders.filter((w) => /^Closed/.test(w.status)).length,
    ),
    'FLEET.length': String(FLEET.length),
    'predictedUsage().length': String(usage.length),
    'B.kb.length': String(ctx.kb.length),

    // ---- loop 4 · commercial ----------------------------------------
    'fmtk(revenue().parts12)': fmtk(rev.parts12),
    'fmtk(revenue().labour10w)': fmtk(rev.labour10w),
    "FLEET.filter(f=>/call-off/.test(mxOf(f.machine_id)?.servicecontract?.type||'')&&!(f.battery)).length":
      String(callOffMachines(ctx)),
    'upgradeCandidates().length': String(upgradeCandidateRows(ctx).length),
    'renewals()[0].contractnum': first?.contractnum ?? '—',
    'renewals()[0].customer': first?.customer ?? '—',
    'renewals()[0].days': first ? String(first.days) : '—',

    // ---- loop 5 · demand & supply planning --------------------------
    'predictedUsage().reduce((a,r)=>a+r.demand,0)': String(
      usage.reduce((a, r) => a + r.demand, 0),
    ),
    'pmDue(30).length': String(pmDue(30, ctx).length),
    '[...new Set(B.inventory.map(i=>i.location))].length': String(inventoryLocations.size),
    'B.orders.filter(o=>/agent/i.test(o.status)).length': String(
      ctx.orders.filter((o) => /agent/i.test(o.status)).length,
    ),
    'predictedUsage().filter(r=>/Transfer/.test(r.prop)).length': String(
      usage.filter((r) => /Transfer/.test(r.prop)).length,
    ),
    'B.roster.rows.length': String(ctx.roster.length),
    'meanUtil()': String(meanUtil(ctx)),
  };
}

/**
 * Render one `LiveFigure` template to text.
 *
 * An unbound expression renders as an em dash: the sentence around it stays
 * readable and the gap is visible, which is the behaviour a reviewer can act
 * on. `values` is `null` while the data is still loading, and every expression
 * then renders as `…`.
 */
export function figureText(
  figure: LiveFigure,
  values: Readonly<Record<string, string>> | null,
): string {
  return figure
    .map((segment) =>
      'text' in segment ? segment.text : values ? (values[segment.expr] ?? '—') : '…',
    )
    .join('');
}

/**
 * The hero's four figures — demo `its.slice(0,4)`, each shown as its leading
 * token plus the remainder truncated to 58 characters.
 */
export interface LoopHeroStat {
  readonly eyebrow: string;
  readonly value: string;
  readonly detail: string;
}

export function loopHeroStats(
  loop: OemLoop,
  values: Readonly<Record<string, string>> | null,
): LoopHeroStat[] {
  return loop.items.slice(0, 4).map((item) => {
    const text = figureText(item.figure, values);
    const lead = text.split(' ')[0];
    // The demo bolds the leading token unconditionally, which on the four
    // prose-only figures ("core routing on the exchange units · …") bolds a
    // stray word and reads as a broken number. A leading token with no digit
    // in it is not a figure, so the tile shows the row's delivery status
    // instead and gives the whole sentence to the detail line.
    const isFigure = /\d/.test(lead);
    return {
      eyebrow: item.what,
      value: isFigure ? lead : MODULE_STATUS_TOKENS[item.status].label,
      detail: isFigure ? text.replace(/^[^ ]+ /, '').slice(0, 58) : text.slice(0, 92),
    };
  });
}

// -------------------------------------------------------------------------
// Panels
// -------------------------------------------------------------------------

/** A cell in a rebuilt loop panel. */
export type LoopCellKind = 'text' | 'mono' | 'numeric' | 'band' | 'machine' | 'part';

export interface LoopCell {
  readonly kind: LoopCellKind;
  readonly text: string;
  /** Second line under the cell, where the demo printed one. */
  readonly sub?: string;
  /** Machine id on a `machine` cell; part number on a `part` cell. */
  readonly key?: string;
  /** Condition band on a `band` cell. */
  readonly band?: ConditionBand;
  /** The demo drew this figure in the critical colour. */
  readonly critical?: boolean;
}

/** One rebuilt panel: the seed's eyebrow and columns, with real rows. */
export interface LoopTablePanel {
  readonly eyebrow: string;
  readonly cols: readonly LoopColumn[];
  readonly rows: readonly (readonly LoopCell[])[];
}

const text = (t: string, sub?: string): LoopCell => ({ kind: 'text', text: t, sub });
const mono = (t: string): LoopCell => ({ kind: 'mono', text: t });
const num = (t: string, critical = false): LoopCell => ({
  kind: 'numeric',
  text: t,
  critical,
});
const machine = (id: string): LoopCell => ({
  kind: 'machine',
  text: id,
  key: id,
  sub: BY_ID[id] ? MSHORT[BY_ID[id].mine] : undefined,
});

/**
 * Every table panel of a loop, rebuilt from the loaded data.
 *
 * The chip panel on loop 2 is not here: its chips open lake documents, which
 * needs a hook, so the scene owns it. The order of the returned panels matches
 * the seed's `panels` array with the chip panel removed.
 */
export function loopTablePanels(loop: OemLoop, ctx: LoopCtx): LoopTablePanel[] {
  const tables = loop.panels.filter(
    (p): p is Extract<typeof p, { kind: 'table' }> => p.kind === 'table',
  );

  return tables.map((panel) => ({
    eyebrow: panel.eyebrow,
    cols: panel.cols,
    rows: panelRows(panel.rowsExpr, ctx),
  }));
}

/**
 * Rows for one panel, dispatched on the seed's `rowsExpr` — the demo's own
 * derivation, carried verbatim and used here as the case label.
 */
function panelRows(rowsExpr: string, ctx: LoopCtx): (readonly LoopCell[])[] {
  switch (rowsExpr) {
    // ---- loop 1 · exchange units ------------------------------------
    case 'EXCHANGE.map(e=>[e[0],e[1],e[2],e[3]])':
      return EXCHANGE.map((e) => [
        { kind: 'part' as const, text: e.part_no, key: e.part_no },
        text(e.description),
        machine(e.where.split(' ')[0]),
        text(e.note),
      ]);

    // ---- loop 1 · components closest to their interval --------------
    case 'HEALTH.map(h=>({...h,iv:intervalOf(h.component),rem:intervalOf(h.component)-h.hours_since_change})).filter(h=>h.rem<400).sort((a,b)=>a.rem-b.rem).slice(0,16)':
      return HEALTH.map((h) => ({
        row: h,
        iv: intervalOf(h.component),
        rem: intervalOf(h.component) - h.hours_since_change,
      }))
        .filter((h) => h.rem < 400)
        .sort((a, b) => a.rem - b.rem)
        .slice(0, 16)
        .map(({ row, iv, rem }) => [
          machine(row.machine_id),
          text(row.component),
          num(groupNum(row.hours_since_change)),
          num(groupNum(iv)),
          // Demo: `h.rem<0 ? 'overdue '+(-h.rem) : h.rem` + ' h', in crit red.
          num(rem < 0 ? `overdue ${groupNum(-rem)} h` : `${groupNum(rem)} h`, rem < 0),
          {
            kind: 'band' as const,
            text: CONDITION_BAND_LABEL[healthBand(row)],
            band: healthBand(row),
          },
        ]);

    // ---- loop 2 · warranty & campaign cases -------------------------
    case 'B.cases.filter(c=>/Warranty|Incident/.test(c.type))':
      return ctx.cases
        .filter((c) => /Warranty|Incident/.test(c.type))
        .map((c) => [
          mono(c.case_no),
          machine(c.machine_id),
          text(c.type),
          {
            kind: 'band' as const,
            text: c.status.split(' — ')[0],
            band: caseStatusBand(c.status),
          },
          text(c.subject),
        ]);

    // ---- loop 3 · fleet rules ---------------------------------------
    case 'FLEET_RULES':
      return FLEET_RULES.map((r) => [text(r[0]), mono(r[1]), text(r[2])]);

    // ---- loop 4 · contract renewal horizon --------------------------
    case 'renewals()':
      return renewals(ctx).map((c) => [
        mono(c.contractnum),
        text(c.customer, c.contractor ?? undefined),
        text(c.type),
        num(String(c.machines)),
        mono(c.end),
        num(String(c.days), c.days <= 180),
        // Demo literals, in the demo's order.
        text(
          c.days <= 180
            ? 'renewal proposal drafted'
            : /call-off/.test(c.type)
              ? 'upgrade candidate'
              : '—',
        ),
      ]);

    // ---- loop 4 · upgrade candidates --------------------------------
    case 'upgradeCandidates()':
      return upgradeCandidateRows(ctx).map((r) => [
        { kind: 'machine' as const, text: r.machine, key: r.machine, sub: r.model },
        text(r.site),
        { kind: 'band' as const, text: r.bandLabel, band: r.band },
        text(r.worstComponent),
        text(r.terms),
        text(r.proposal),
      ]);

    // ---- loop 5 · predicted usage against the shelf -----------------
    case 'predictedUsage().slice(0,12)':
      return predictedUsage(ctx)
        .slice(0, 12)
        .map((r) => [
          { kind: 'part' as const, text: r.part_no, key: r.part_no, sub: r.desc },
          text(MSHORT[r.mine]),
          num(String(r.demand)),
          num(String(r.store), r.gap > 0),
          num(String(r.dcq)),
          text(r.prop),
        ]);

    default:
      return [];
  }
}

/**
 * The demo's case-status pill class (`/Declined/ → vpoor`, `/Open/ → fair`,
 * otherwise `good`), expressed as a condition band so the band recipes in
 * `styles/oem.css` carry it.
 */
function caseStatusBand(status: string): ConditionBand {
  if (/Declined/.test(status)) return 'vpoor';
  if (/Open/.test(status)) return 'fair';
  return 'good';
}

/** The bulletin chips on loop 2 — demo `B.bulletins.map(…)`. */
export interface BulletinChip {
  readonly id: string;
  /** Title, truncated as the demo truncates it. */
  readonly label: string;
  /** Lake file name, for `useLakeDoc`. */
  readonly file: string;
}

export const bulletinChips = (bulletins: readonly Bulletin[]): BulletinChip[] =>
  bulletins.map((b) => ({
    id: b.id,
    label: b.title.length > 52 ? `${b.title.slice(0, 50)}…` : b.title,
    file: b.file,
  }));

/** The other four loops, for the "The other loops" footer. */
export const siblingLoops = (loop: OemLoop, all: readonly OemLoop[]) =>
  all.filter((l) => l.id !== loop.id).map((l) => ({ id: l.id, name: l.name }));

/** Site display name for a mine code, for the record drawer. */
export const siteName = (mine: MineCode): string => MINES[mine].name;

// -------------------------------------------------------------------------
// `audit(table, filter)` — the rows behind a loop item
// -------------------------------------------------------------------------

/** How many rows an audit slice shows before it says how many more there are. */
export const AUDIT_SLICE_LIMIT = 50;

/** The `oem-source` drawer payload for one `audit()` item row. */
export interface AuditSlice {
  readonly kind: 'table';
  readonly title: string;
  readonly columns: string[];
  readonly rows: (string | number | null)[][];
  readonly provenance: string;
}

/** Does one row satisfy one clause of a seed `audit()` filter? */
function matches(value: unknown, clause: { op: string; value: string }): boolean {
  const s = value == null ? '' : String(value);
  if (clause.op === 'eq') return s === clause.value;
  if (clause.op === 'startsWith') return s.startsWith(clause.value);
  return new RegExp(clause.value).test(s);
}

/**
 * The demo's `audit(table, filter)`, as a drawer payload.
 *
 * Three datasets are reachable from the loop pages — the condition register,
 * the service cases and the work-order register — and each is rendered with
 * the columns the demo's own audit view prints for it. An unknown table name
 * returns `null` and the row falls back to being inert rather than opening an
 * empty drawer.
 */
export function auditSlice(
  table: string,
  filter: readonly { field: string; op: string; value: string }[],
  ctx: LoopCtx,
): AuditSlice | null {
  const keep = <T extends Record<string, unknown>>(rows: readonly T[]): T[] =>
    rows.filter((row) => filter.every((c) => matches(row[c.field], c)));

  if (table === 'health') {
    const rows = keep(HEALTH as unknown as readonly Record<string, unknown>[]);
    return slice(
      'Condition register',
      ['Machine', 'Component', 'Score', 'Band', 'Hours since change'],
      rows.map((h) => [
        String(h.machine_id),
        String(h.component),
        Number(h.health_score),
        CONDITION_BAND_LABEL[healthBand(h as never)],
        Number(h.hours_since_change),
      ]),
      `Condition register · component_health.csv · ${rows.length} of ${HEALTH.length} rows`,
    );
  }

  if (table === 'cases') {
    const rows = keep(ctx.cases as unknown as readonly Record<string, unknown>[]);
    return slice(
      'Service cases',
      ['Case', 'Opened', 'Machine', 'Type', 'Status', 'Subject'],
      rows.map((c) => [
        String(c.case_no),
        String(c.opened),
        String(c.machine_id),
        String(c.type),
        String(c.status),
        String(c.subject),
      ]),
      `Salesforce · service_cases.csv · ${rows.length} of ${ctx.cases.length} rows`,
    );
  }

  if (table === 'wos') {
    const rows = keep(ctx.workOrders as unknown as readonly Record<string, unknown>[]);
    return slice(
      'Work orders',
      ['Work order', 'Created', 'Machine', 'Type', 'Status', 'Component', 'Description'],
      rows.map((w) => [
        String(w.wo_id),
        String(w.created).slice(0, 10),
        String(w.machine_id),
        String(w.wo_type),
        String(w.status),
        String(w.component ?? ''),
        String(w.description ?? ''),
      ]),
      `SAP PM · work_orders.csv · ${rows.length} of ${groupNum(ctx.workOrders.length)} rows`,
    );
  }

  return null;
}

function slice(
  title: string,
  columns: string[],
  rows: (string | number | null)[][],
  provenance: string,
): AuditSlice {
  return {
    kind: 'table',
    title,
    columns,
    rows: rows.slice(0, AUDIT_SLICE_LIMIT),
    provenance:
      rows.length > AUDIT_SLICE_LIMIT
        ? `${provenance} · first ${AUDIT_SLICE_LIMIT} shown`
        : provenance,
  };
}
